import {
  Button,
  Heading,
  Modal,
  ModalBody,
  Text,
} from "@growtherapy/sprout-ui";
import { minutesToMilliseconds } from "date-fns";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import HandHoldingPencil from "../../assets/images/hand-holding-pencil.svg?react";
import { useGetTelehealthSessionInfo } from "../../hooks/useGetTelehealthSessionInfo";
import { useTrackEvent } from "../../segment/segment";
import { EventTypeEnum } from "../../segment/types";
import { providerConsentAtom } from "../../session/messages/state";
import { ClientTranscriptionUserAction } from "../../session/types";
import { sessionInfoAtom } from "../../state";
import { toast } from "../../toasts";
import { UserType } from "../../types";
import { ConsentStatus, participantCountAtom } from "../../video-sdk";
import { useSendVisitorParticipantStateCallback } from "../messages/useSendVisitorParticipantState";
import { ConsentModalText } from "./constants";
import { useGracefullyRecordClientTranscriptionConsentCallback } from "./useGracefullyRecordClientTranscriptionConsentCallback";
import { useShowTranscriptionStorageDisclaimer } from "./useShowTranscriptionStorageDisclaimer";
import { useToggleRecordingCallback } from "./useToggleRecordingCallback";

export type ConsentModalProps = {
  onDeclineConsent: () => void;
  onConsent: () => Promise<void>;
  onDismissConsent: () => void;
  isLoading: boolean;
  isOpen: boolean;
  autoDismissTimeout?: number;
  providerName: string;
  isMultiPatientSession: boolean;
  showTranscriptionStorageDisclaimer: boolean;
};

export function ConsentModal({
  onDeclineConsent,
  onConsent,
  onDismissConsent,
  isLoading,
  isOpen,
  providerName,
  isMultiPatientSession,
  showTranscriptionStorageDisclaimer,
  autoDismissTimeout = minutesToMilliseconds(2),
}: ConsentModalProps) {
  useEffect(
    function dismissAfterTwoMinutes() {
      if (!isOpen) return;
      const timeout = setTimeout(function dismissModal() {
        onDismissConsent();
      }, autoDismissTimeout);
      return function cleanup() {
        clearTimeout(timeout);
      };
    },
    [isOpen, autoDismissTimeout, onDismissConsent],
  );

  const consentFooterText = showTranscriptionStorageDisclaimer
    ? `${ConsentModalText.TRANSCRIPTION_STORAGE_DISCLAIMER} ${ConsentModalText.FOOTER}`
    : ConsentModalText.FOOTER;
  return (
    <Modal
      onClose={onDismissConsent}
      isOpen={isOpen}
      className="shadow-none sm:max-w-[31rem] md:max-w-[31rem] sm:top-[25%]"
      onOutsideClick={onDismissConsent}
    >
      <ModalBody className="flex flex-col items-center gap-4 p-8 text-center">
        <HandHoldingPencil />
        <div className="flex flex-col gap-2 items-center">
          <Heading variant="md" as="h2">
            {providerName} {ConsentModalText.HEADING}
          </Heading>
        </div>
        <div className="w-full flex flex-col items-center text-center">
          {isMultiPatientSession && (
            <Text variant="sm">
              {ConsentModalText.MULTI_PARTICIPANT_DISCLAIMER}
            </Text>
          )}
          <Text variant="sm" className="text-neutral-900">
            {consentFooterText}
          </Text>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 w-full">
          <Button
            disabled={isLoading}
            onClick={onDeclineConsent}
            use="secondary"
            className="w-full"
          >
            {ConsentModalText.DONT_ENABLE_BUTTON}
          </Button>
          <Button
            loading={isLoading}
            onClick={onConsent}
            use="primary"
            className="w-full m-0"
          >
            {ConsentModalText.ENABLE_BUTTON}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}

enum RecordConsentResponse {
  "CONSENTED",
  "ERROR",
  "CONSENTED_AND_RECORDING",
}

function useRecordConsentCallback() {
  const { userClicked } = useTrackEvent();
  const getSessionInfo = useAtomCallback(
    useCallback((get) => get(sessionInfoAtom), []),
  );
  const getProviderConsent = useAtomCallback(
    useCallback((get) => get(providerConsentAtom).value, []),
  );
  const toggleRecording = useToggleRecordingCallback();
  const { recordClientConsentCallback: recordConsent, isLoading } =
    useGracefullyRecordClientTranscriptionConsentCallback();

  const recordConsentCallback = useCallback(
    async (isOptedIn: boolean, userAction?: ClientTranscriptionUserAction) => {
      const sessionInfo = getSessionInfo();
      const appointmentShortId = sessionInfo?.appointment?.shortId;
      userClicked(EventTypeEnum.GAVE_TRANSCRIPTION_CONSENT, {
        appointmentShortId: appointmentShortId!,
        entityType: UserType.CLIENT,
      });

      const recordingResult = await recordConsent(isOptedIn, userAction);

      if (recordingResult.status === "failure") {
        toast.error(
          "An unexpected error occurred while trying to record your consent. Please try again.",
        );
        return RecordConsentResponse.ERROR;
      }

      if (recordingResult.status === "success" && isOptedIn) {
        // Start recording if provider has consented
        const providerConsent = getProviderConsent();
        if (providerConsent === ConsentStatus.OPTED_IN) {
          toggleRecording(true);
          return RecordConsentResponse.CONSENTED_AND_RECORDING;
        }
      }

      return RecordConsentResponse.CONSENTED;
    },
    [
      getProviderConsent,
      getSessionInfo,
      recordConsent,
      toggleRecording,
      userClicked,
    ],
  );

  return {
    recordConsentCallback,
    isLoading,
  };
}

export function ConsentModalWrapper({
  onClose,
  onStartRecording,
  setShouldShowClientConsentDeclinedToast,
  isOpen,
  setPatientConsent,
}: {
  onClose: () => void;
  onStartRecording: () => void;
  setShouldShowClientConsentDeclinedToast: (shouldShow: boolean) => void;
  isOpen: boolean;
  setPatientConsent: ({
    value,
    lastUpdated,
  }: {
    value: ConsentStatus;
    lastUpdated: number;
  }) => void;
}) {
  const participantCount = useAtomValue(participantCountAtom);
  const { providerShortId, patientShortId } = useParams();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const providerName =
    data?.telehealthSessionInfo?.provider?.name ??
    ConsentModalText.PROVIDER_NAME_FALLBACK;
  const sendParticipantState = useSendVisitorParticipantStateCallback();
  const showTranscriptionStorageDisclaimer =
    useShowTranscriptionStorageDisclaimer();

  const { recordConsentCallback: recordConsent, isLoading } =
    useRecordConsentCallback();
  const onConsent = useCallback(async () => {
    recordConsent(true).then((consentResult) => {
      if (consentResult === RecordConsentResponse.CONSENTED_AND_RECORDING) {
        onStartRecording();
      }
      if (consentResult !== RecordConsentResponse.ERROR) {
        onClose();
      }
    });
  }, [recordConsent, onStartRecording, onClose]);

  // Dismiss modal and set consent to SOFT_NO and show toast
  const declineConsent = useCallback(() => {
    setPatientConsent({
      value: ConsentStatus.SOFT_NO,
      lastUpdated: Date.now(),
    });
    recordConsent(false, ClientTranscriptionUserAction.REJECT).then(
      (consentResult) => {
        if (consentResult !== RecordConsentResponse.ERROR) {
          sendParticipantState();
          setShouldShowClientConsentDeclinedToast(true);
          onClose();
        }
      },
    );
  }, [
    sendParticipantState,
    setPatientConsent,
    recordConsent,
    setShouldShowClientConsentDeclinedToast,
    onClose,
  ]);

  // Dismiss modal and set consent to SOFT_NO WITHOUT showing toast
  const dismissConsent = useCallback(() => {
    setPatientConsent({
      value: ConsentStatus.SOFT_NO,
      lastUpdated: Date.now(),
    });
    recordConsent(false, ClientTranscriptionUserAction.DISMISS).then(
      (consentResult) => {
        if (consentResult !== RecordConsentResponse.ERROR) {
          sendParticipantState();
          setShouldShowClientConsentDeclinedToast(true);
          onClose();
        }
      },
    );
  }, [
    sendParticipantState,
    setPatientConsent,
    recordConsent,
    setShouldShowClientConsentDeclinedToast,
    onClose,
  ]);

  return (
    <ConsentModal
      onDismissConsent={dismissConsent}
      onDeclineConsent={declineConsent}
      isLoading={isLoading}
      isOpen={isOpen}
      providerName={providerName}
      isMultiPatientSession={participantCount > 1}
      showTranscriptionStorageDisclaimer={showTranscriptionStorageDisclaimer}
      onConsent={onConsent}
    />
  );
}
